import * as React from 'react';
// Import hooks.
import {
    useInputPerilDeductible,
    UseInputPerilDeductibleResults,
} from '@app/hooks/view/input';
// Import api models.
import {
    PerilDeductible,
    PossiblePeril,
} from '@app/models/api';
// Import props.
import {
    FormCoverageLimitsAndDeductiblesProps,
    FormInputSettingsCoverageLimitsAndDeductiblesProps,
} from '@app/props';

interface UseSubformPerilsDeductiblesParameters {
    id: string;
    inputSettingsProps?: FormInputSettingsCoverageLimitsAndDeductiblesProps;
    deductibleProps?: FormCoverageLimitsAndDeductiblesProps.DeductiblesProps;
    allowEdits: boolean;
    showValidations?: boolean;
}

interface UseSubformPerilsDeductiblesResults {
    areAllInputsDisabled: boolean
    showPerilDeductibleInputs: boolean;
    selectedPerilDeductibles: PerilDeductible[];
    perilDeductibleInputs: UseInputPerilDeductibleResults;
    checkFormValidation: (focusToElement: boolean) => boolean;
    flagAllInputsToShowValidations: () => void;
    doesFormHaveChanges: () => boolean;
}

const useSubformPerilsDeductibles = (params: UseSubformPerilsDeductiblesParameters): UseSubformPerilsDeductiblesResults => {
    
	// #region exported methods

    /**
     * Method to check form validation with focus.
     **/
    const checkFormValidation = (focusToElement: boolean): boolean => {
        return (
            perilDeductibleInputs.checkValidation(focusToElement)
        );
    };

    /**
     * Flags all inputs to show the validation messages.
     **/
    const flagAllInputsToShowValidations = (): void => {
        perilDeductibleInputs.changeShowValidationFlag(true);
    };

    /**
     * Checks to see if the form has changes. 
     **/
    const doesFormHaveChanges = (): boolean => {
        return perilDeductibleInputs.doPerilDeductiblesHaveChanges();
    }

    // #endregion exported methods

    // #region private methods

    // #region id generators

    /**
    * Generates base id. 
    **/
    const generateBaseId = (): string => {
        return params.id;
    }

    /**
    * Generates id for the perils deductibles. 
    **/
    const generateIdForPerilsDeductibles = (): string => {
        return `${generateBaseId()}-perilsDeductibles`;
    }

    // #endregion id generators

    // #endregion private methods

    // #region variables

    const possiblePerils: PossiblePeril[] = React.useMemo(() => params.deductibleProps?.possiblePerils ?? [], [params.deductibleProps?.possiblePerils]);
    const selectedPerilDeductibles: PerilDeductible[] = React.useMemo(() => params.deductibleProps?.selectedPerilDeductibles ?? [], [params.deductibleProps?.selectedPerilDeductibles]);
    const areSelectedOrPossiblePerilsNotEmpty: boolean = React.useMemo(() => possiblePerils.length > 0 || selectedPerilDeductibles.length > 0, [possiblePerils, selectedPerilDeductibles]);

    // #region Input settings visibility.

    const showPerilDeductibleInputs: boolean = React.useMemo(() =>
        params.inputSettingsProps?.perilDeductibleInputs !== 'hidden'
        && areSelectedOrPossiblePerilsNotEmpty,
        [
            params.inputSettingsProps?.perilDeductibleInputs,
            areSelectedOrPossiblePerilsNotEmpty,
        ]
    );

    // #endregion Input settings visibility.

    // #region Input settings disable.

    const arePerilDeductibleInputsDisabled: boolean = React.useMemo(() =>
        !params.allowEdits
        || !showPerilDeductibleInputs
        || params.inputSettingsProps?.perilDeductibleInputs === 'disabled',
        [
            params.allowEdits,
            showPerilDeductibleInputs,
            params.inputSettingsProps?.perilDeductibleInputs
        ]
    );

    // #endregion Input settings disable.

    // Setup the perils deductibles.
    const perilDeductibleInputs: UseInputPerilDeductibleResults = useInputPerilDeductible({
        id: generateIdForPerilsDeductibles(),
        disabled: arePerilDeductibleInputsDisabled,
        required: true,
        possiblePerils: possiblePerils,
        selectedPerilDeductibles: selectedPerilDeductibles,
        showValidation: params.showValidations,
    });

    // Check to see if all inputs are disabled.
    const areAllInputsDisabled: boolean = React.useMemo(() => {
        return perilDeductibleInputs.isDisabled;
    }, [perilDeductibleInputs.isDisabled]);

    // #endregion variables

    return {
        areAllInputsDisabled,
        showPerilDeductibleInputs,
        selectedPerilDeductibles,
        perilDeductibleInputs,
        checkFormValidation,
        flagAllInputsToShowValidations,
        doesFormHaveChanges,
    };
};

// Export parameters.
export { UseSubformPerilsDeductiblesParameters };

// Export results.
export { UseSubformPerilsDeductiblesResults };

export default useSubformPerilsDeductibles;
